import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { useWallet } from '../../hooks/useWallet';
import { useSHIDOPrice } from '../../hooks/useSHIDOPrice';
import { useSNS } from '../../hooks/useSNS';
import { CHAIN_CONFIGS } from '../../utils/constants';
import { useWeb3Modal } from '@web3modal/wagmi/react';

export const Header: React.FC = () => {
  const { walletInfo, connectWallet, disconnectWallet, displayAddress, isConnecting } = useWallet();
  const { snsName } = useSNS(walletInfo.address);
  const { formattedPrice, formattedChange, isPositive, loading: priceLoading } = useSHIDOPrice();
  const { open } = useWeb3Modal();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get current chain name
  const currentChainName = walletInfo.chainId 
    ? CHAIN_CONFIGS[Object.keys(CHAIN_CONFIGS).find(key => 
        CHAIN_CONFIGS[key].chainId === walletInfo.chainId
      ) || '']?.name || 'Unknown Network'
    : '';
    
  // Navigation links
  const navLinks = [
    { path: '/', label: 'Bridge' },
    { path: '/portfolio', label: 'Portfolio' }
  ];
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header 
      className="sticky top-0 z-50 border-b border-white/5"
      style={{
        background: 'rgba(5, 5, 16, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-10">
            {/* Logo and Title */}
            <div 
              className="flex items-center gap-3 cursor-pointer md:cursor-default group"
              onClick={() => {
                if (window.innerWidth < 768) {
                  toggleMobileMenu();
                }
              }}
            >
              {/* Logo with glow effect */}
              <div className="relative">
                <div 
                  className="absolute inset-0 rounded-xl blur-md opacity-50 group-hover:opacity-70 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 242, 0.4), rgba(0, 31, 89, 0.6))'
                  }}
                />
                <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center border border-white/10 shadow-lg">
                  <img src="/images/wshido.png" alt="Shido Bridge" className="w-7 h-7" />
                  
                  {/* Mobile menu indicator */}
                  <div className="md:hidden absolute -right-1 -bottom-1 w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-2.5 w-2.5 text-white transition-transform duration-300 ${mobileMenuOpen ? 'rotate-180' : 'rotate-0'}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div>
                <h1 className="text-xl font-bold tracking-tight">
                  <span className="bg-gradient-to-r from-white via-primary-200 to-primary-400 bg-clip-text text-transparent">
                    Shido Bridge
                  </span>
                </h1>
                <p className="text-xs text-gray-500 font-medium">Bridge native SHIDO to Ethereum</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300
                    ${location.pathname === link.path
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  {location.pathname === link.path && (
                    <span 
                      className="absolute inset-0 rounded-lg"
                      style={{
                        background: 'linear-gradient(135deg, rgba(6, 182, 242, 0.15), rgba(0, 31, 89, 0.2))',
                        border: '1px solid rgba(6, 182, 242, 0.2)'
                      }}
                    />
                  )}
                  <span className="relative">{link.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* SHIDO Price + Wallet Section */}
          <div className="flex items-center gap-3">
            {/* SHIDO Price Display */}
            {!priceLoading && (
              <div 
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)'
                }}
              >
                <img src="/images/wshido.png" alt="SHIDO" className="w-4 h-4" />
                <span className="text-gray-300">{formattedPrice}</span>
                <span className={`${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isPositive ? '↑' : '↓'} {formattedChange}
                </span>
              </div>
            )}
            
            {walletInfo.isConnected ? (
              <>
                {/* Network Badge */}
                <div 
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  {currentChainName}
                </div>
                
                {/* Wallet Address with SNS */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => open()}
                  className="flex items-center gap-2"
                >
                  {snsName ? (
                    <span className="text-primary-400 font-medium">{snsName}</span>
                  ) : (
                    displayAddress
                  )}
                </Button>
                
                {/* Disconnect Button */}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={disconnectWallet}
                  title="Disconnect wallet"
                  className="text-gray-400 hover:text-red-400"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </Button>
              </>
            ) : (
              <Button 
                onClick={connectWallet} 
                isLoading={isConnecting}
                size="sm"
                glow
              >
                Connect Wallet
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden border-t border-white/5 animate-fade-in"
          style={{
            background: 'rgba(5, 5, 16, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
        >
          <div className="container mx-auto px-4 py-3">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
                    ${location.pathname === link.path
                      ? 'text-white bg-white/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
